/**
 * Settings > Service bundles > editor (tracker item 85): name and description,
 * the active / portal / Auto-renew-by-default toggles and the ordered
 * components (item picker, quantity, days after the previous visit, notes,
 * reorder, remove) with a live timeline preview. Saving replaces the
 * component list; visits already created are never touched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { createAPIClient, IBundlesApi, IItemsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageBundles } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { BUNDLES_SETTINGS_PATH, timelineOffsets } from '../../features/service-bundles/bundleUi';
import { isBundleItem, loadAllItems } from '../../features/service-bundles/bundleItems';

type EditorApi = IBundlesApi & Pick<IItemsApi, 'listItems'>;

export interface ComponentDraft {
  key: string;
  itemId: string;
  quantity: string;
  daysAfterPrevious: string;
  notes: string;
}

export interface BundleDraft {
  name: string;
  description: string;
  isActive: boolean;
  portalRequestable: boolean;
  autoRenewDefault: boolean;
  components: ComponentDraft[];
}

let keySeed = 0;
export const newComponent = (patch: Partial<ComponentDraft> = {}): ComponentDraft => ({ key: `c${(keySeed += 1)}`, itemId: '', quantity: '1', daysAfterPrevious: '0', notes: '', ...patch });

export const emptyDraft = (): BundleDraft => ({ name: '', description: '', isActive: true, portalRequestable: false, autoRenewDefault: false, components: [newComponent()] });

export function draftOf(bundle: types.ServiceBundle): BundleDraft {
  return {
    name: bundle.name,
    description: bundle.description ?? '',
    isActive: bundle.isActive,
    portalRequestable: bundle.portalRequestable,
    autoRenewDefault: bundle.autoRenewDefault,
    components: bundle.components.map((c) => newComponent({ itemId: c.itemId, quantity: String(Number(c.quantity)), daysAfterPrevious: String(c.daysAfterPrevious), notes: c.notes ?? '' })),
  };
}

/** The request body for a draft, or the first problem as text. */
export function bundlePayload(draft: BundleDraft): { payload: types.CreateServiceBundleRequest | null; problem: string | null } {
  const name = draft.name.trim();
  if (!name) return { payload: null, problem: 'Enter a name for the bundle.' };
  if (draft.components.length === 0) return { payload: null, problem: 'Add at least one visit.' };
  const components: types.BundleComponentInput[] = [];
  for (let i = 0; i < draft.components.length; i += 1) {
    const c = draft.components[i];
    const label = `Visit ${i + 1}`;
    if (!c.itemId) return { payload: null, problem: `${label}: choose an item.` };
    const quantity = Number(c.quantity);
    if (c.quantity.trim() === '' || !Number.isFinite(quantity) || quantity <= 0) return { payload: null, problem: `${label}: quantity must be more than 0.` };
    const gap = i === 0 ? 0 : Number(c.daysAfterPrevious);
    if (i > 0 && (c.daysAfterPrevious.trim() === '' || !Number.isInteger(gap) || gap < 0 || gap > 3650)) return { payload: null, problem: `${label}: days after the previous visit must be a whole number from 0 to 3650.` };
    components.push({ itemId: c.itemId, quantity: c.quantity.trim(), daysAfterPrevious: gap, notes: c.notes.trim() || null });
  }
  return {
    payload: { name, description: draft.description.trim() || null, isActive: draft.isActive, portalRequestable: draft.portalRequestable, autoRenewDefault: draft.autoRenewDefault, components },
    problem: null,
  };
}

export function ServiceBundleEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient() as EditorApi, []);
  const navigate = useNavigate();
  const allowed = canManageBundles(user);
  const [draft, setDraft] = useState<BundleDraft | null>(id ? null : emptyDraft());
  const [items, setItems] = useState<types.Item[] | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    loadAllItems(api).then((list) => { if (active) setItems(list); }).catch((err) => { if (active) { setItems([]); setLoadError(getErrorMessage(err, 'Could not load the items')); } });
    if (id) {
      api.getServiceBundle(id).then((bundle) => { if (active) setDraft(draftOf(bundle)); }).catch((err) => { if (active) setLoadError(getErrorMessage(err, 'Could not load the bundle')); });
    }
    return () => { active = false; };
  }, [api, id, allowed]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Service bundles are available to admins and office managers.</p>;
  if (!draft) {
    return <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">{loadError ? <><Toast kind="error">{loadError}</Toast><p className="text-gray-700">The bundle could not be loaded.</p></> : <p role="status">Loading bundle...</p>}<Link to={BUNDLES_SETTINGS_PATH} className="text-blue-700 underline">Service bundles</Link></main>;
  }

  const eligible = (items ?? []).filter(isBundleItem);
  const term = filter.trim().toLowerCase();
  const visibleItems = (selectedId: string) => eligible.filter((i) => i.id === selectedId || !term || i.name.toLowerCase().includes(term));
  const nameOf = (itemId: string) => items?.find((i) => i.id === itemId)?.name ?? '';
  const offsets = timelineOffsets(draft.components.map((c) => ({ daysAfterPrevious: Number(c.daysAfterPrevious) })));

  const set = (patch: Partial<BundleDraft>) => setDraft({ ...draft, ...patch });
  const setComponent = (key: string, patch: Partial<ComponentDraft>) => set({ components: draft.components.map((c) => (c.key === key ? { ...c, ...patch } : c)) });
  const move = (index: number, by: -1 | 1) => {
    const next = [...draft.components];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    set({ components: next });
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const { payload, problem } = bundlePayload(draft);
    if (!payload) { setError(problem); return; }
    setBusy(true);
    setError(null);
    try {
      if (id) await api.updateServiceBundle(id, payload);
      else await api.createServiceBundle(payload);
      navigate(BUNDLES_SETTINGS_PATH);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the bundle'));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id || busy || !window.confirm(`Delete ${draft.name}? Enrollments and visits already created are not affected.`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteServiceBundle(id);
      navigate(BUNDLES_SETTINGS_PATH);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not delete the bundle'));
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
      <Link to={BUNDLES_SETTINGS_PATH} className="text-sm text-blue-700 hover:underline">← Service bundles</Link>
      <h1 className="mt-2 text-2xl font-bold text-gray-900">{id ? 'Edit service bundle' : 'New service bundle'}</h1>
      {id && <p className="mt-1 text-sm text-gray-600">Changes apply to bundles added from now on. Visits already created keep their items, dates and prices.</p>}
      {error && <Toast kind="error">{error}</Toast>}
      {loadError && <Toast kind="error">{loadError}</Toast>}
      <form onSubmit={save} className="mt-6 space-y-6" noValidate>
        <section className="space-y-4 rounded-lg bg-white p-5 shadow sm:p-6">
          <div>
            <label htmlFor="bundle-name" className={labelClass}>Name</label>
            <input id="bundle-name" className={inputClass} value={draft.name} maxLength={255} disabled={busy} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div>
            <label htmlFor="bundle-description" className={labelClass}>Description (optional)</label>
            <textarea id="bundle-description" className={inputClass} rows={2} maxLength={2000} value={draft.description} disabled={busy} onChange={(e) => set({ description: e.target.value })} />
          </div>
          <div className="space-y-3">
            <label className="flex items-start gap-3"><input id="bundle-active" type="checkbox" className="mt-1 h-5 w-5" checked={draft.isActive} disabled={busy} onChange={(e) => set({ isActive: e.target.checked })} /><span><span className="font-medium text-gray-900">Active</span><span className="block text-sm text-gray-600">Inactive bundles cannot be added to properties.</span></span></label>
            <label className="flex items-start gap-3"><input id="bundle-portal" type="checkbox" className="mt-1 h-5 w-5" checked={draft.portalRequestable} disabled={busy} onChange={(e) => set({ portalRequestable: e.target.checked })} /><span><span className="font-medium text-gray-900">Offer in the customer portal</span><span className="block text-sm text-gray-600">Customers can request it next to individual services; staff approve each request.</span></span></label>
            <label className="flex items-start gap-3"><input id="bundle-autorenew" type="checkbox" className="mt-1 h-5 w-5" checked={draft.autoRenewDefault} disabled={busy} onChange={(e) => set({ autoRenewDefault: e.target.checked })} /><span><span className="font-medium text-gray-900">Auto-renew by default</span><span className="block text-sm text-gray-600">New enrollments start with auto-renew on. It can be changed per enrollment, and changing this never changes existing enrollments.</span></span></label>
          </div>
        </section>

        <section className="rounded-lg bg-white p-5 shadow sm:p-6" aria-label="Visits">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Visits</h2>
            <div className="w-full sm:w-64">
              <label htmlFor="item-filter" className="sr-only">Filter items</label>
              <input id="item-filter" className={inputClass} placeholder="Filter the item lists" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
          </div>
          <ol className="space-y-4">
            {draft.components.map((c, index) => (
              <li key={c.key} className="rounded-lg border border-gray-200 p-3 sm:p-4" data-testid="component-row">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold text-gray-900">Visit {index + 1}</p>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" className={secondaryButtonClass} disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`Move visit ${index + 1} up`}>Up</button>
                    <button type="button" className={secondaryButtonClass} disabled={busy || index === draft.components.length - 1} onClick={() => move(index, 1)} aria-label={`Move visit ${index + 1} down`}>Down</button>
                    <button type="button" className={secondaryButtonClass} disabled={busy || draft.components.length === 1} onClick={() => set({ components: draft.components.filter((x) => x.key !== c.key) })} aria-label={`Remove visit ${index + 1}`}>Remove</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label htmlFor={`${c.key}-item`} className={labelClass}>Item</label>
                    <select id={`${c.key}-item`} className={inputClass} value={c.itemId} disabled={busy || items === null} onChange={(e) => setComponent(c.key, { itemId: e.target.value })}>
                      <option value="">{items === null ? 'Loading items...' : 'Choose an item'}</option>
                      {c.itemId && !eligible.some((i) => i.id === c.itemId) && <option value={c.itemId}>{nameOf(c.itemId) || 'Unavailable item'}</option>}
                      {visibleItems(c.itemId).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor={`${c.key}-qty`} className={labelClass}>Quantity</label>
                    <input id={`${c.key}-qty`} className={inputClass} type="number" min="0.01" step="any" value={c.quantity} disabled={busy} onChange={(e) => setComponent(c.key, { quantity: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor={`${c.key}-gap`} className={labelClass}>Days after previous visit</label>
                    <input id={`${c.key}-gap`} className={inputClass} type="number" min="0" max="3650" step="1" value={index === 0 ? '0' : c.daysAfterPrevious} disabled={busy || index === 0} onChange={(e) => setComponent(c.key, { daysAfterPrevious: e.target.value })} />
                    {index === 0 && <p className="mt-1 text-xs text-gray-500">The first visit falls on the start date.</p>}
                  </div>
                  <div className="sm:col-span-6">
                    <label htmlFor={`${c.key}-notes`} className={labelClass}>Notes (optional)</label>
                    <input id={`${c.key}-notes`} className={inputClass} maxLength={500} value={c.notes} disabled={busy} onChange={(e) => setComponent(c.key, { notes: e.target.value })} />
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <button type="button" className={`${secondaryButtonClass} mt-4`} disabled={busy || draft.components.length >= 50} onClick={() => set({ components: [...draft.components, newComponent({ daysAfterPrevious: '30' })] })}>Add a visit</button>
        </section>

        <section className="rounded-lg bg-white p-5 shadow sm:p-6" aria-label="Timeline preview">
          <h2 className="text-lg font-semibold text-gray-900">Timeline preview</h2>
          <ol className="mt-3 space-y-2 text-sm" data-testid="timeline-preview">
            {draft.components.map((c, index) => (
              <li key={c.key} className="flex items-baseline gap-3">
                <span className="w-20 shrink-0 font-semibold text-blue-800">Day {offsets[index]}</span>
                <span className="break-words text-gray-800">{nameOf(c.itemId) || 'Choose an item'}{Number(c.quantity) !== 1 && c.quantity ? ` × ${c.quantity}` : ''}</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-sm text-gray-600">{draft.components.length} visit{draft.components.length === 1 ? '' : 's'}{draft.components.length > 1 ? ` over ${offsets[offsets.length - 1]} days` : ''}. Dates that fall on a non-work day move to the next work day when the bundle is added.</p>
        </section>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {id ? <button type="button" className="py-2 text-sm font-semibold text-red-700 hover:underline" disabled={busy} onClick={remove}>Delete bundle</button> : <span />}
          <div className="flex gap-2">
            <Link to={BUNDLES_SETTINGS_PATH} className={`${secondaryButtonClass} inline-flex items-center`}>Cancel</Link>
            <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Saving...' : 'Save bundle'}</button>
          </div>
        </div>
      </form>
    </main>
  );
}
